import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';
import useSearchMessages from '@/hooks/search/use-search-messages';
import { parseSessionKey } from '@/utils/session';

export default function useSearchMessagesBySession(sessionKey: string, keyword = '') {
  const [type, id] = parseSessionKey(sessionKey);
  const [searchKeyword, setSearchKeyword] = useState(keyword);
  const { t } = useTranslation();

  const [debouncedKeyword] = useDebounce(searchKeyword, 600);
  const { messages: result, isLoading, clear: clearResults } = useSearchMessages(debouncedKeyword, { type, id });
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { keyword },
  });

  const submit = ({ keyword }: FormValues) => {
    setSearchKeyword(keyword);
    return keyword;
  };

  const clear = () => {
    setSearchKeyword('');
    clearResults().catch(console.warn);
    form.resetField('keyword', { defaultValue: '' });
  };

  const helperText = useMemo(() => {
    if (isLoading) {
      return t('search.searching');
    }

    if (form.formState.isSubmitted && result.length === 0) {
      return t('search.no-result');
    }

    return '';
  }, [result, isLoading, form.formState.isSubmitted, t]);

  useEffect(() => {
    if (keyword) {
      form.setValue('keyword', keyword);
      submit({ keyword });
    }
  }, [keyword, form.setValue]);

  return {
    form,
    searchKeyword,
    submit,
    clear,
    result,
    isLoading,
    helperText,
  };
}

const schema = z.object({
  keyword: z
    .string()
    .min(1)
    .transform((value) => value.trim()),
});

type FormValues = z.infer<typeof schema>;
